import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import type { AgentDirectoryItem, DetectionStats } from '../../lib/api/admin.ts'
import { tenant } from '../../lib/api/tenant.ts'
import type { TenantAgentCounts, TenantSummary } from '../../lib/api/tenant.ts'
import { agentStatePresentation } from '../../lib/agents/state.ts'
import { useAuth } from '../../lib/auth/AuthContext.tsx'
import { buildThreatOverview, bucketSize, hasFailed } from '../../lib/detections/overview.ts'
import type { ThreatOverview } from '../../lib/detections/overview.ts'
import { responseOutcomeLabel, severityPresentation } from '../../lib/detections/semantics.ts'
import { formatDayClock, formatInt, formatMinutes } from '../../lib/format.ts'
import { agentHref } from '../../lib/logs/investigation.ts'
import { Link, navigate } from '../../lib/router.tsx'
import type { DetectionAlert } from '../../lib/sse/types.ts'
import { usePolledResource } from '../../lib/usePolledResource.ts'
import { useResource } from '../../lib/useResource.ts'
import { PageHeader } from '../../components/layout/PageHeader.tsx'
import { Badge } from '../../components/primitives/Badge.tsx'
import { Button } from '../../components/primitives/Button.tsx'
import { Metric } from '../../components/primitives/Metric.tsx'
import type { Tone } from '../../components/primitives/Metric.tsx'
import { Panel } from '../../components/primitives/Panel.tsx'
import { ErrorState, LoadingRow, Notice } from '../../components/primitives/States.tsx'
import { RankedBars } from '../../components/detections/Panorama.tsx'
import { WindowPicker, windowRange } from '../../components/stats/DetectionStatsPanel.tsx'
import type { StatsWindow } from '../../components/stats/DetectionStatsPanel.tsx'
import { ThreatChart } from '../../components/stats/ThreatChart.tsx'

/** Resumo e listas: poll de 30–60 s (§5.2). */
const POLL_MS = 45_000

/**
 * O diretório de agentes não filtra por estado (`/api/tenant/agents` aceita só
 * página e busca, §5.3), então os silenciosos são filtrados aqui, sobre a maior
 * página que o servidor aceita. Empresas com mais de 200 agentes veem só os
 * silenciosos desta página — a contagem exata vem do resumo, que é completo.
 */
const AGENT_PAGE = 200

/**
 * Teto da lista de alertas agregada na tela: 5 páginas de 200. Acima disso os
 * números derivados da lista valem para os mais recentes, e a tela avisa.
 */
const ALERT_PAGE = 200
const ALERT_PAGES = 5

const WINDOW_PHRASE: Record<StatsWindow, string> = {
  '24h': 'nas últimas 24 h',
  '7d': 'nos últimos 7 dias',
  '30d': 'nos últimos 30 dias',
}

interface Threats {
  stats: DetectionStats
  /** A janela imediatamente anterior, do mesmo tamanho. `null` se a consulta falhou. */
  previous: DetectionStats | null
  overview: ThreatOverview
  hourly: boolean
}

async function loadAlerts(range: { from: string; to: string }, signal: AbortSignal) {
  const alerts: DetectionAlert[] = []
  let cursor: string | null = null
  for (let page = 0; page < ALERT_PAGES; page += 1) {
    const result = await tenant.detections({ ...range, cursor, pageSize: ALERT_PAGE }, signal)
    alerts.push(...result.items)
    if (!result.has_more || result.next_cursor === null) return { alerts, truncated: false }
    cursor = result.next_cursor
  }
  return { alerts, truncated: true }
}

async function loadThreats(window: StatsWindow, signal: AbortSignal): Promise<Threats> {
  const range = windowRange(window)
  const from = Date.parse(range.from)
  const to = Date.parse(range.to)
  const before = { from: new Date(from - (to - from)).toISOString(), to: range.from }
  const [stats, previous, list] = await Promise.all([
    tenant.detectionStats(range, signal),
    tenant.detectionStats(before, signal).catch(() => null),
    loadAlerts(range, signal),
  ])
  return {
    stats,
    previous,
    overview: buildThreatOverview(list.alerts, { from, to }, list.truncated),
    hourly: bucketSize(to - from) < 24 * 3_600_000,
  }
}

/**
 * Visão geral da empresa — a tela que o CLIENTE abre primeiro.
 *
 * Responde três perguntas, nesta ordem: estou seguro? o que aconteceu? o que
 * foi feito por mim? Nada de plataforma (fila, latência, volume de eventos):
 * isso é da Black Ghost e não aparece aqui.
 *
 * A lista de alertas lê a tabela de ingestão, e o contrato é "só por ação
 * humana": ela carrega ao abrir, ao trocar a janela e no botão Atualizar —
 * nunca num poll. O resumo dos agentes segue no poll de sempre.
 */
export function Overview() {
  const { companyName } = useAuth()
  const [window, setWindow] = useState<StatsWindow>('7d')

  const summaryFetcher = useCallback((signal: AbortSignal) => tenant.summary(signal), [])
  const summary = usePolledResource<TenantSummary>(summaryFetcher, POLL_MS)

  const agentsFetcher = useCallback(
    (signal: AbortSignal) => tenant.agents({ page: 1, pageSize: AGENT_PAGE }, signal),
    [],
  )
  const agents = usePolledResource(agentsFetcher, POLL_MS)

  const threatsFetcher = useCallback((signal: AbortSignal) => loadThreats(window, signal), [window])
  const threats = useResource(threatsFetcher)

  const silent = useMemo<AgentDirectoryItem[]>(
    () =>
      (agents.data?.items ?? [])
        .filter((item) => item.state === 'anomalous_silence' || item.state === 'never_connected')
        .sort(
          (a, b) =>
            agentStatePresentation(a.state).rank - agentStatePresentation(b.state).rank ||
            (b.silent_for_minutes ?? 0) - (a.silent_for_minutes ?? 0),
        ),
    [agents.data],
  )

  const data = summary.data
  const title = data?.tenant_name ?? companyName ?? 'Visão geral'
  const t = threats.data
  // Trocar a janela mantém o dado anterior na tela até o novo chegar.
  const stale = threats.loading && t !== null

  const refresh = () => {
    summary.reload()
    agents.reload()
    threats.reload()
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-canvas">
      <PageHeader
        title={title}
        actions={
          <>
            <WindowPicker value={window} onChange={setWindow} />
            <Button size="sm" onClick={refresh} busy={threats.loading}>
              Atualizar
            </Button>
          </>
        }
      />

      {data?.directory_stale === true && (
        <div className="px-3 pt-2">
          <Notice tone="warn">
            Dado atrasado: números de agentes de {formatDayClock(data.directory_captured_at)} UTC.
          </Notice>
        </div>
      )}

      {summary.error !== null && data === null ? (
        <div className="p-3">
          <ErrorState error={summary.error} onRetry={summary.reload} />
        </div>
      ) : data === null ? (
        <LoadingRow label="Montando a visão geral…" />
      ) : (
        <main
          className={`grid grid-cols-1 content-start gap-px border-b border-line-subtle bg-line-subtle transition-opacity sm:grid-cols-2 xl:grid-cols-12 ${
            stale ? 'opacity-60' : ''
          }`}
        >
          <StatusHero
            agents={data.agents}
            threats={t}
            window={window}
            className="sm:col-span-2 xl:col-span-12"
          />

          <KpiCell className="xl:col-span-3">
            <Metric
              label="máquinas protegidas"
              value={formatInt(data.agents.connected)}
              unit={`/ ${formatInt(data.agents.total)}`}
              size="xl"
              tone={data.agents.connected > 0 ? 'ok' : 'neutral'}
              note={coverageNote(data.agents)}
            />
          </KpiCell>
          <KpiCell className="xl:col-span-3">
            <Metric
              label="ameaças detectadas"
              value={t === null ? '—' : formatInt(t.stats.totals.total)}
              size="xl"
              note={t === null ? WINDOW_PHRASE[window] : trendNote(t)}
            />
          </KpiCell>
          <KpiCell className="xl:col-span-3">
            <Metric
              label="contidas automaticamente"
              value={t === null ? '—' : formatInt(t.overview.contained)}
              size="xl"
              tone={t !== null && t.overview.contained > 0 ? 'ok' : 'neutral'}
              note={t === null ? 'sem intervenção humana' : containedNote(t.overview)}
            />
          </KpiCell>
          <KpiCell className="xl:col-span-3">
            <Metric
              label="pedem sua atenção"
              value={t === null ? '—' : formatInt(attention(t.overview))}
              size="xl"
              tone={t !== null && attention(t.overview) > 0 ? 'crit' : 'neutral'}
              note={
                t === null ? (
                  'contenção falhou ou crítica sem resposta'
                ) : attention(t.overview) > 0 ? (
                  <Link href="/deteccoes?responseStatus=failed" className="underline hover:text-fg">
                    ver na fila de detecções
                  </Link>
                ) : (
                  'tudo tratado'
                )
              }
            />
          </KpiCell>

          {threats.error !== null && t === null ? (
            <div className="bg-surface p-3 sm:col-span-2 xl:col-span-12">
              <ErrorState error={threats.error} onRetry={threats.reload} />
            </div>
          ) : t === null ? (
            <div className="bg-surface sm:col-span-2 xl:col-span-12">
              <LoadingRow label="Contando as ameaças da janela…" />
            </div>
          ) : (
            <>
              <Panel
                title={t.hourly ? 'Ameaças por hora' : 'Ameaças por dia'}
                className="sm:col-span-2 xl:col-span-8"
                meta={<SeverityLegend />}
              >
                {t.overview.loaded === 0 ? (
                  <EmptyCalm window={window} />
                ) : (
                  <ThreatChart buckets={t.overview.buckets} hourly={t.hourly} />
                )}
                {t.overview.truncated && <TruncatedNote loaded={t.overview.loaded} />}
              </Panel>

              <Panel title="O que tentaram fazer" className="sm:col-span-2 xl:col-span-4">
                <TacticList tactics={t.overview.tactics} />
              </Panel>

              <Panel title="Máquinas mais visadas" className="xl:col-span-5">
                <RankedBars
                  items={t.overview.hosts}
                  mono
                  empty="Nenhuma máquina foi alvo na janela."
                  onPick={(item) => navigate(`/deteccoes?endpointId=${encodeURIComponent(item.key)}`)}
                />
                <SilentAgents
                  silent={silent}
                  loading={agents.data === null && agents.error === null}
                  stale={agents.data?.stale === true}
                />
              </Panel>

              <Panel
                title="Últimas detecções"
                flush
                className="xl:col-span-7"
                meta={
                  <Link href="/deteccoes" className="underline hover:text-fg">
                    abrir a fila
                  </Link>
                }
              >
                <RecentList alerts={t.overview.recent} />
              </Panel>
            </>
          )}
        </main>
      )}
    </div>
  )
}

/* ── status ──────────────────────────────────────────────────────────────── */

const attention = (o: ThreatOverview) => o.failed + o.criticalOpen

const HERO_TONE: Record<'ok' | 'warn' | 'crit', { ring: string; text: string; icon: string }> = {
  ok: { ring: 'border-ok', text: 'text-ok', icon: '✓' },
  warn: { ring: 'border-warn', text: 'text-warn', icon: '!' },
  crit: { ring: 'border-crit', text: 'text-crit', icon: '!' },
}

/**
 * A frase do topo: o veredito em português, antes de qualquer número. Risco
 * vem das ameaças sem contenção; atenção, de máquina que parou de falar.
 */
function StatusHero({
  agents,
  threats,
  window,
  className,
}: {
  agents: TenantAgentCounts
  threats: Threats | null
  window: StatsWindow
  className: string
}) {
  const offline = agents.anomalous_silence + agents.never_connected
  const o = threats?.overview ?? null
  const pending = o === null ? 0 : attention(o)

  const tone: 'ok' | 'warn' | 'crit' = pending > 0 ? 'crit' : offline > 0 ? 'warn' : 'ok'
  const headline =
    tone === 'crit'
      ? pending === 1
        ? 'Uma ameaça precisa da sua atenção'
        : `${formatInt(pending)} ameaças precisam da sua atenção`
      : tone === 'warn'
        ? offline === 1
          ? 'Uma máquina está sem comunicação'
          : `${formatInt(offline)} máquinas estão sem comunicação`
        : 'Sua empresa está protegida'

  const total = threats?.stats.totals.total ?? null
  const phrase = WINDOW_PHRASE[window]
  const story =
    total === null || o === null
      ? 'Contando as ameaças da janela…'
      : total === 0
        ? `Nenhuma ameaça detectada ${phrase}.`
        : `${capitalize(phrase)}, ${formatInt(total)} ${total === 1 ? 'ameaça foi detectada' : 'ameaças foram detectadas'} e ${formatInt(o.contained)} ${o.contained === 1 ? 'foi contida' : 'foram contidas'} automaticamente pelo agente.`
  const look = HERO_TONE[tone]
  return (
    <section aria-label="Status de proteção" className={`flex items-center gap-4 bg-surface px-4 py-4 ${className}`}>
      <span
        aria-hidden
        className={`flex size-11 shrink-0 items-center justify-center rounded-full border-2 text-lg font-medium ${look.ring} ${look.text}`}
      >
        {look.icon}
      </span>
      <div className="min-w-0">
        <h2 className={`text-lg font-medium ${tone === 'ok' ? 'text-fg' : look.text}`}>{headline}</h2>
        <p className="text-sm text-fg-secondary">{story}</p>
      </div>
    </section>
  )
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

function KpiCell({ className, children }: { className: string; children: ReactNode }) {
  return <div className={`bg-surface px-4 py-3 ${className}`}>{children}</div>
}

function coverageNote(agents: TenantAgentCounts) {
  const offline = agents.anomalous_silence + agents.never_connected
  if (agents.total === 0) return 'nenhum agente instalado'
  if (offline > 0) return <span className="text-warn">{formatInt(offline)} sem comunicação</span>
  return `${Math.round((agents.connected / agents.total) * 100)}% conectadas agora`
}

function trendNote(t: Threats) {
  if (t.previous === null) return 'sem comparação com o período anterior'
  const delta = t.stats.totals.total - t.previous.totals.total
  if (delta === 0) return 'igual ao período anterior'
  return `${delta > 0 ? '↑' : '↓'} ${formatInt(Math.abs(delta))} vs período anterior`
}

function containedNote(o: ThreatOverview) {
  if (o.loaded === 0) return 'nenhuma ameaça para conter'
  const pct = `${Math.round((o.contained / o.loaded) * 100)}% das ameaças`
  return o.truncated ? `${pct} · nas ${formatInt(o.loaded)} mais recentes` : pct
}

/* ── painéis ─────────────────────────────────────────────────────────────── */

function SeverityLegend() {
  return (
    <span className="flex items-center gap-2">
      {(['critical', 'high', 'medium', 'low'] as const).map((severity) => {
        const p = severityPresentation(severity)
        return (
          <span key={severity} className="flex items-center gap-1">
            <span aria-hidden className={`inline-block size-1.5 ${p.fill}`} />
            {p.label.toLowerCase()}
          </span>
        )
      })}
    </span>
  )
}

function EmptyCalm({ window }: { window: StatsWindow }) {
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-1 text-center">
      <span className="text-sm text-fg">Nenhuma ameaça {WINDOW_PHRASE[window]}</span>
    </div>
  )
}

function TruncatedNote({ loaded }: { loaded: number }) {
  return (
    <p className="mt-2 text-micro text-fg-muted">
      Gráfico e rankings: só as {formatInt(loaded)} mais recentes.
    </p>
  )
}

function TacticList({ tactics }: { tactics: ThreatOverview['tactics'] }) {
  if (tactics.length === 0) {
    return <p className="py-2 text-sm text-fg-muted">Nenhuma tentativa identificada na janela.</p>
  }
  const shown = tactics.slice(0, 6)
  const max = Math.max(...shown.map((t) => t.count))
  return (
    <ol className="flex flex-col gap-2">
      {shown.map((tactic) => (
        <li key={tactic.id} className="flex flex-col gap-0.5" title={tactic.id}>
          <span className="flex items-baseline justify-between gap-2">
            <span className="min-w-0 truncate text-sm text-fg">{tactic.label}</span>
            <span className="tnum shrink-0 font-mono text-micro text-fg-secondary">{tactic.count}</span>
          </span>
          <span className="block h-1 w-full bg-inset">
            <span className="block h-full bg-accent" style={{ width: `${(tactic.count / max) * 100}%` }} />
          </span>
        </li>
      ))}
    </ol>
  )
}

function SilentAgents({ silent, loading, stale }: { silent: AgentDirectoryItem[]; loading: boolean; stale: boolean }) {
  if (loading || silent.length === 0) return null
  return (
    <div className="mt-4 border-t border-line-subtle pt-3">
      <h3 className="mb-1.5 flex items-center gap-2 text-micro font-medium uppercase tracking-label text-warn">
        Sem comunicação
        {stale && (
          <Badge tone="warn" dot>
            diretório defasado
          </Badge>
        )}
      </h3>
      <ul className="flex flex-col gap-1">
        {silent.slice(0, 5).map((item) => {
          const state = agentStatePresentation(item.state)
          return (
            <li key={item.endpoint_id} className="flex items-center justify-between gap-2 text-sm">
              <Link
                href={agentHref(item.endpoint_id, {})}
                className="min-w-0 truncate font-mono text-fg underline-offset-2 hover:underline"
              >
                {item.hostname ?? item.endpoint_id}
              </Link>
              <span className="flex shrink-0 items-center gap-1.5">
                <Badge tone={state.tone} dot title={state.note}>
                  {state.label}
                </Badge>
                {item.silent_for_minutes !== null && (
                  <span className="tnum text-micro text-fg-muted">há {formatMinutes(item.silent_for_minutes)}</span>
                )}
              </span>
            </li>
          )
        })}
      </ul>
      {silent.length > 5 && (
        <Link href="/agentes" className="mt-1 block text-micro text-fg-muted underline">
          e mais {formatInt(silent.length - 5)} — ver todos os agentes
        </Link>
      )}
    </div>
  )
}

/** O desfecho, em uma palavra que o cliente entende. Falha vence tudo. */
function outcome(alert: DetectionAlert): { label: string; tone: Tone; dashed: boolean } {
  if (hasFailed(alert)) return { label: 'Contenção falhou', tone: 'crit', dashed: false }
  const done = alert.response.find((r) => r.status === 'executed')
  if (done !== undefined) return { label: responseOutcomeLabel(done.action), tone: 'ok', dashed: false }
  if (alert.response.some((r) => r.status === 'simulated')) {
    return { label: 'Simulado', tone: 'neutral', dashed: true }
  }
  return { label: 'Registrado', tone: 'neutral', dashed: false }
}

function RecentList({ alerts }: { alerts: DetectionAlert[] }) {
  if (alerts.length === 0) {
    return <p className="px-3 py-3 text-sm text-fg-muted">Nenhuma detecção na janela.</p>
  }
  return (
    <ul>
      {alerts.map((alert) => {
        const p = severityPresentation(alert.severity)
        const result = outcome(alert)
        const host = alert.row.hostname ?? alert.host_hostname ?? alert.row.endpoint_id
        const href =
          alert.rule_id === null ? '/deteccoes' : `/deteccoes?ruleId=${encodeURIComponent(alert.rule_id)}`
        return (
          <li key={alert.row.id} className="border-b border-line-subtle last:border-b-0">
            <Link href={href} className="flex items-center gap-3 px-3 py-2 hover:bg-inset">
              <span aria-hidden className={`h-8 w-0.5 shrink-0 ${p.fill}`} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm text-fg">{alert.rule_name ?? 'Detecção sem nome'}</span>
                <span className="truncate text-micro text-fg-muted">
                  <span className={p.text}>{p.label}</span> · <span className="font-mono">{host}</span> ·{' '}
                  <span className="tnum">{formatDayClock(alert.row.received_at)} UTC</span>
                </span>
              </span>
              <Badge tone={result.tone} dashed={result.dashed} dot={result.tone !== 'neutral'}>
                {result.label}
              </Badge>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
